import { GoogleOutlined } from '@ant-design/icons';
import { Button, Spin } from 'antd';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { validateReferralCode } from '../../services/referralApi';
import { SEO_DEFAULTS } from '../../utils/seoConfig';
import './InvitationLandingPage.css';

const InvitationLandingPage = () => {
  const { referralCode } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [validating, setValidating] = useState(true);
  const [validationResult, setValidationResult] = useState(null);

  useEffect(() => {
    const validate = async () => {
      try {
        const response = await validateReferralCode(referralCode);
        setValidationResult(response.data);
      } catch (error) {
        console.error('Failed to validate referral code:', error);
        setValidationResult({ valid: false, reason: 'error' });
      } finally {
        setValidating(false);
      }
    };

    validate();
  }, [referralCode]);

  useEffect(() => {
    // If user is already logged in, redirect
    if (currentUser && validationResult?.valid) {
      // Store referral code in sessionStorage for processing after redirect
      sessionStorage.setItem('referral_code', referralCode);
      // Redirect to new chat page (profile endpoint will process referral)
      setTimeout(() => navigate('/'), 3000);
    }
  }, [currentUser, validationResult, referralCode, navigate]);

  // Store referral code in sessionStorage when user clicks signup
  const handleSignup = (method) => {
    if (validationResult?.valid) {
      sessionStorage.setItem('referral_code', referralCode);
    }

    if (method === 'google') {
      navigate('/signup?provider=google');
    } else {
      navigate('/signup');
    }
  };

  // SEO content based on validation state
  const getSEOContent = () => {
    if (validating) {
      return {
        title: 'Invitation to CellCog',
        description: 'Join CellCog and hire an AI employee for any role.',
      };
    }

    if (!validationResult?.valid || currentUser) {
      return {
        title: 'Join CellCog - Hire an AI Employee for Any Role',
        description: 'Hire AI employees that work real shifts — research, apps, videos, PDFs, and more.',
      };
    }

    // Valid invitation
    const referrerName = validationResult?.referrer_display_name || 'A friend';
    return {
      title: `Join ${referrerName} on CellCog - Get 500 Bonus Credits`,
      description: `${referrerName} invited you to CellCog, where you can hire an AI employee for any role. Sign up, and after your first purchase you'll both receive 500 bonus credits to create reports, apps, videos, and more.`,
    };
  };

  const seoContent = getSEOContent();

  if (validating) {
    return (
      <>
        <Helmet>
          <title>{seoContent.title} | {SEO_DEFAULTS.siteName}</title>
          <meta name="description" content={seoContent.description} />
          <meta name="robots" content="noindex" />
        </Helmet>

        <div className="invitation-landing">
          <div className="invitation-card">
            <Spin size="large" />
            <p>Validating invitation...</p>
          </div>
        </div>
      </>
    );
  }

  // Already logged in - show redirect message
  if (currentUser) {
    return (
      <>
        <Helmet>
          <title>Already a Member | {SEO_DEFAULTS.siteName}</title>
          <meta name="description" content="You're already a CellCog user. Start creating with AI." />
          <meta name="robots" content="noindex" />
        </Helmet>

        <div className="invitation-landing">
          <div className="invitation-card">
            <div className="cellcog-logo">CellCog</div>
            <h1>You're Already a CellCog User!</h1>
            <p>This referral link is for new users only.</p>
            <p className="redirect-message">Redirecting you to CellCog...</p>
            <Button type="primary" size="large" onClick={() => navigate('/')}>
              Go to CellCog Now
            </Button>
          </div>
        </div>
      </>
    );
  }

  // Invalid or expired code
  if (!validationResult?.valid) {
    const getMessage = () => {
      if (validationResult?.reason === 'disabled') {
        return 'This invitation link is no longer active.';
      }
      if (validationResult?.reason === 'expired') {
        return 'This invitation link has expired.';
      }
      return 'This invitation link is invalid or has expired.';
    };

    return (
      <>
        <Helmet>
          <title>Invalid Invitation | {SEO_DEFAULTS.siteName}</title>
          <meta name="description" content="This invitation link is no longer valid. Sign up for CellCog to start creating with AI." />
          <link rel="canonical" href={`${SEO_DEFAULTS.url}/signup`} />
          <meta name="robots" content="noindex" />
        </Helmet>

        <div className="invitation-landing">
          <div className="invitation-card">
            <div className="cellcog-logo">CellCog</div>
            <h1>Invalid Invitation Link</h1>
            <p>{getMessage()}</p>
            <p>You can still sign up for CellCog below!</p>
            <div className="signup-buttons">
              <Button type="primary" size="large" onClick={() => navigate('/signup')}>
                Sign Up Now
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Valid code - show invitation
  const referrerName = validationResult.referrer_display_name;
  const invitationUrl = `${SEO_DEFAULTS.url}/invitation/${referralCode}`;

  return (
    <>
      <Helmet>
        <title>{seoContent.title}</title>
        <meta name="description" content={seoContent.description} />
        <link rel="canonical" href={invitationUrl} />

        {/* Open Graph - Critical for social sharing */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoContent.title} />
        <meta property="og:description" content={seoContent.description} />
        <meta property="og:url" content={invitationUrl} />
        <meta property="og:image" content={SEO_DEFAULTS.image} />
        <meta property="og:site_name" content={SEO_DEFAULTS.siteName} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoContent.title} />
        <meta name="twitter:description" content={seoContent.description} />
        <meta name="twitter:image" content={SEO_DEFAULTS.image} />
        <meta name="twitter:site" content={SEO_DEFAULTS.twitterHandle} />
      </Helmet>

      <div className="invitation-landing">
        <div className="invitation-card">
          <div className="cellcog-logo">CellCog</div>

          <div className="invitation-icon">🎁</div>

          <h1>You've Been Invited to CellCog!</h1>

          <p className="invitation-message">
            <strong>{referrerName}</strong> invited you to try CellCog,
            where you can hire an AI employee for any role. Sign up, and after your
            first purchase you'll both receive <strong>500 bonus credits</strong>!
          </p>

          <div className="signup-buttons">
            <Button
              type="primary"
              size="large"
              icon={<GoogleOutlined />}
              onClick={() => handleSignup('google')}
              block
            >
              Sign Up with Google
            </Button>

            <Button
              size="large"
              onClick={() => handleSignup('email')}
              block
            >
              Sign Up with Email
            </Button>
          </div>

          <p className="login-link">
            Already have an account?{' '}
            <a href="/login" onClick={(e) => { e.preventDefault(); navigate('/login'); }}>
              Log in
            </a>
          </p>

          {/* Program Terms */}
          <div className="invitation-terms">
            <p>
              By signing up, you agree to our referral program terms. Fraudulent activity
              may result in account suspension and credit forfeiture.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default InvitationLandingPage;
